import { useContext, useEffect, useRef, useState } from "react";
import { VariantContextData } from "../VariantBuilderContext";
import useActionCable from "../../shared/useActionCable";
import apiRequest from "../../../utils/apiRequest";
import serializeBuilderSnapshot from "./serializeBuilderSnapshot";
import applyAssistantOperations from "./applyAssistantOperations";
import aiFocusBreadcrumb from "./aiFocusBreadcrumb";
import {
  billableActionsFromResponse,
  dispatchBillableActionsUsed,
} from "../../../utils/billableActionsMeter";
import { createChatLiveness } from "../../../utils/chatLiveness";

// module-level store so chat history survives panel tab switches
const chatSessions = {};

const REQUEST_TIMEOUT_MS = 120000;

const FOCUSABLE_OBJECT_TYPES = [
  "steps",
  "sections",
  "section_columns",
  "elements",
  "fields",
];

function messageContentForApi(message) {
  const parts = [];
  if (message.content) parts.push(message.content);
  const question = message.clarifyingQuestion;
  if (question && question.question) {
    parts.push(question.question);
    if (question.options && question.options.length) {
      parts.push(`Options: ${question.options.join(" / ")}`);
    }
  }
  return parts.join("\n");
}

function getSession(variant_id) {
  if (!chatSessions[variant_id]) {
    chatSessions[variant_id] = {
      messages: [
        { role: "assistant", content: "What can I build for you today?" },
      ],
      syncId: Math.random().toString(36).substring(2, 15),
      focus: null,
      unread: 0,
      listeners: [],
    };
  }
  return chatSessions[variant_id];
}

function emitSessionChange(session) {
  session.listeners.forEach((listener) => listener());
}

function subscribeToSession(session, listener) {
  session.listeners.push(listener);
  return () => {
    session.listeners = session.listeners.filter((l) => l !== listener);
  };
}

// called from the AI tab button, so the current selection (which builder.view
// clears) is captured as the chat's focused object
export function setAiChatFocus(variant_id, object_type, object_id) {
  const session = getSession(variant_id);

  if (
    object_type &&
    object_id &&
    FOCUSABLE_OBJECT_TYPES.includes(object_type)
  ) {
    session.focus = { object_type: object_type, object_id: object_id };
    emitSessionChange(session);
  }
}

// unread assistant replies (arrived while the user was on another tab);
// used by the AI tab button to render its notification badge
export function useAiChatUnread(variant_id) {
  const session = getSession(variant_id);
  const [unread, setUnread] = useState(session.unread);

  useEffect(
    () => subscribeToSession(session, () => setUnread(session.unread)),
    [variant_id]
  );

  return unread;
}

const useAiAssistant = () => {
  const [contextData, builder] = useContext(VariantContextData);
  const variant = contextData.objects.variant;
  const session = getSession(variant.id);

  const [messages, setMessages] = useState(session.messages);
  const [loading, setLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState(null);
  const [focus, setFocusState] = useState(session.focus);

  const setFocus = (newFocus) => {
    session.focus = newFocus;
    setFocusState(newFocus);
    emitSessionChange(session);
  };

  // pick up focus set from the AI tab button (the panel stays mounted)
  useEffect(
    () => subscribeToSession(session, () => setFocusState(session.focus)),
    [variant.id]
  );

  // refs so the ActionCable callback always sees the latest builder state
  const builderRef = useRef();
  const contextDataRef = useRef();
  builderRef.current = builder;
  contextDataRef.current = contextData;

  // Re-armable liveness deadline (playbook chat pattern): progress frames keep
  // resetting it so a slow-but-alive job survives, while a dead worker or lost
  // broadcast surfaces an error instead of leaving the dots spinning forever.
  const loadingRef = useRef(false);
  const chatLivenessRef = useRef(null);

  const stopLiveness = () => {
    if (chatLivenessRef.current) {
      chatLivenessRef.current.dispose();
      chatLivenessRef.current = null;
    }
  };

  const startLiveness = () => {
    stopLiveness();
    chatLivenessRef.current = createChatLiveness({
      timeoutMs: REQUEST_TIMEOUT_MS,
      isActive: () => loadingRef.current,
      onTimeout: () => {
        stopLiveness();
        if (!loadingRef.current) return;
        loadingRef.current = false;
        setLoading(false);
        setProgressLabel(null);
        appendMessage({
          role: "assistant",
          content: "ConvertFlow AI stopped responding. Please try again.",
          error: true,
        });
      },
    });
  };

  const rearmLivenessTimeout = () => {
    if (chatLivenessRef.current) chatLivenessRef.current.bump();
    else startLiveness();
  };

  useEffect(() => stopLiveness, []);

  // clear the unread badge whenever the chat tab is visible
  useEffect(() => {
    if (contextData.view === "ai" && session.unread > 0) {
      session.unread = 0;
      emitSessionChange(session);
    }
  }, [contextData.view, messages.length]);

  const appendMessage = (message) => {
    session.messages = [...session.messages, message];
    setMessages(session.messages);

    // badge the AI tab when a reply lands while the user is on another tab
    if (message.role === "assistant" && contextDataRef.current.view !== "ai") {
      session.unread += 1;
      emitSessionChange(session);
    }
  };

  const handleReceived = async (data) => {
    if (!data) return;

    if (data.status === "progress") {
      if (data.message) {
        appendMessage({ role: "assistant", content: data.message });
      }
      if (data.label) setProgressLabel(data.label);
      if (loadingRef.current) rearmLivenessTimeout();
      return;
    }

    if (!["ready", "failed"].includes(data.status)) return;

    stopLiveness();
    loadingRef.current = false;
    setLoading(false);
    setProgressLabel(null);

    if (data.status === "failed") {
      appendMessage({
        role: "assistant",
        content: data.error || "Something went wrong. Please try again.",
        error: true,
      });
      return;
    }

    dispatchBillableActionsUsed(billableActionsFromResponse(data));

    let applied = null;
    if (Array.isArray(data.operations) && data.operations.length > 0) {
      applied = await applyAssistantOperations(data.operations, {
        builder: builderRef.current,
        contextData: contextDataRef.current,
      });
    }

    // objects imported server-side from assets (steps, sections, or row
    // elements) arrive as full props; register them as unsaved drafts
    // (toBeCreated) grouped into one undo-able history entry
    const importedByType = {
      steps: data.imported_steps,
      sections: data.imported_sections,
      elements: data.imported_elements,
    };
    const importUpdates = [];
    let firstImported = null;

    Object.entries(importedByType).forEach(([object_type, objects]) => {
      if (!Array.isArray(objects) || objects.length === 0) return;

      objects.forEach((object) => {
        builderRef.current.addBuilderObjectFromApiToBuilder(
          object_type,
          object,
          true
        );
        importUpdates.push({
          object_type: object_type,
          object_id: object.id,
          setting_name: "[toBeDeleted]",
          value: undefined,
          old_value: true,
        });
        if (!firstImported) {
          firstImported = { object_type: object_type, object_id: object.id };
        }
      });
    });

    if (importUpdates.length > 0) {
      builderRef.current.update(importUpdates);
      builderRef.current.scrollToObjectWithRetry(
        firstImported.object_type,
        firstImported.object_id
      );
    }

    let content = (data.message || "").trim();
    if (!content && data.summary) {
      content = data.summary;
    }
    if (!content && (applied || importUpdates.length > 0)) {
      content = "Done! Review the changes in the builder and save when ready.";
    }
    if (!content && data.clarifying_question) {
      content = "";
    }
    if (!content && !data.clarifying_question) {
      content = "I wasn't able to make that change. Try rephrasing?";
    }

    if (applied && applied.errors.length > 0) {
      content += `\n\nNote: ${applied.errors.length} change(s) could not be applied.`;
    }

    appendMessage({
      role: "assistant",
      content: content,
      clarifyingQuestion: data.clarifying_question || null,
      applied: applied,
    });
  };

  useActionCable(
    "AsyncModalRequestConfirmationChannel",
    { id: session.syncId },
    {
      onReceived: handleReceived,
    }
  );

  const sendMessage = async (prompt, attachments = []) => {
    const text = (prompt || "").trim();
    if (!text || loading) return;

    // resolve the focused object's breadcrumb at send time; drop the focus
    // if the object no longer exists in the builder
    let focusedObject = null;
    if (session.focus) {
      const breadcrumb = aiFocusBreadcrumb(
        contextDataRef.current,
        builderRef.current,
        session.focus
      );
      if (breadcrumb) {
        focusedObject = { ...session.focus, label: breadcrumb };
      } else {
        setFocus(null);
      }
    }

    appendMessage({
      role: "user",
      content: text,
      attachments: attachments,
      focus: focusedObject,
    });
    loadingRef.current = true;
    setLoading(true);
    setProgressLabel(null);
    startLiveness();

    const history = session.messages
      .slice(0, -1)
      .filter((message) => !message.error)
      .map((message) => ({
        role: message.role,
        content: messageContentForApi(message),
      }))
      .filter((message) => message.content);

    // attachments persist for the whole chat: re-send earlier uploads with
    // every request, deduped by type+name (newest wins); the controller
    // enforces the attachment cap
    const chatAttachments = session.messages
      .filter((message) => message.role === "user")
      .flatMap((message) => message.attachments || []);
    const attachmentsByKey = new Map();
    chatAttachments.forEach((attachment) => {
      attachmentsByKey.set(`${attachment.type}|${attachment.name}`, attachment);
    });
    const mergedAttachments = [...attachmentsByKey.values()];

    const response = await apiRequest("POST", "./assistant", {
      prompt: text,
      messages: history,
      snapshot: serializeBuilderSnapshot(contextDataRef.current),
      attachments: mergedAttachments,
      focused_object: focusedObject,
      cable_sync_id: session.syncId,
      page_url: window.location.href,
    });

    if (response.code !== 200) {
      stopLiveness();
      loadingRef.current = false;
      setLoading(false);
      appendMessage({
        role: "assistant",
        content:
          (response.json && response.json.error) ||
          "Unable to reach ConvertFlow AI. Please try again.",
        error: true,
      });
    }
  };

  // thumbs up/down on an assistant reply; records the conversation dump to the
  // user (syncs to HubSpot -> Slack alert for the product team)
  const sendFeedback = async (messageIndex, rating) => {
    const message = session.messages[messageIndex];
    if (!message || message.role !== "assistant" || message.feedback) return;

    const setFeedback = (value) => {
      session.messages = session.messages.map((m, i) =>
        i === messageIndex ? { ...m, feedback: value } : m
      );
      setMessages(session.messages);
      emitSessionChange(session);
    };

    setFeedback(rating);

    const conversation = session.messages
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }))
      .filter((m) => m.content);

    const response = await apiRequest("POST", "/ai_chat_feedbacks", {
      source: "builder",
      link: window.location.href,
      rating: rating,
      messages: conversation,
    });

    // revert so the user can retry if recording failed
    if (response.code !== 200) setFeedback(null);
  };

  return {
    messages: messages,
    loading: loading,
    progressLabel: progressLabel,
    sendMessage: sendMessage,
    sendFeedback: sendFeedback,
    focus: focus,
    setFocus: setFocus,
  };
};

export default useAiAssistant;
